<script>
  import { _ } from 'svelte-i18n'
  import { createEventDispatcher } from 'svelte'
  import { detected_fonts, user_storefronts } from '../stores'
  import Button from '../elements/Button.svelte'
  
  export let is_editing = false
  export let is_deleting = false

  let selected_to_delete;

  const dispatch = createEventDispatcher()
  
  const handleDelete = (font) => {
    dispatch("fontDelete", font)
  }
  
  // Get uploaded fonts from detected_fonts store and sort by display name
  $: uploaded_fonts = ($detected_fonts || [])
    .filter(font => font.uploaded && font.file_name)
    .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))

  // Check if any fonts cannot be deleted (used in storefronts)
  $: hasUndeletableFonts = uploaded_fonts.some(font => !canDelete(font))

  // Get storefront names for a font
  const getStorefrontNames = (font) => {
    if (!font.storefronts || font.storefronts.length === 0) {
      return '<span class="faded">None</span>'
    }
    
    const storefrontNames = font.storefronts.map(sf => {
      const storefront = ($user_storefronts || []).find(s => s.id === sf.id)
      return storefront ? storefront.name : 'Unknown'
    })
    
    return storefrontNames.join(', ')
  }

  // Check if font can be deleted (not associated with any storefronts)
  const canDelete = (font) => {
    return !font.storefronts || font.storefronts.length === 0
  }

  // Format upload date from the uploaded_at property
  const formattedUploadDate = (font) => {
    // Try to get upload date from uploaded_at property if available
    if (font.uploaded_at) {
      try {
        return new Intl.DateTimeFormat("en", {dateStyle: "medium", timeStyle: "short"}).format(new Date(font.uploaded_at))
      } catch (error) {
        console.warn('Error formatting upload date:', error)
      }
    }
    return 'Unknown'
  }
</script>

{#if hasUndeletableFonts}
  <p class="warning">
    {@html $_('modals.manageUploadedFonts.chooseToDelete')}
  </p>
{/if}

<div class="header row">
  <h4>FONT NAME</h4>
  <h4>STOREFRONTS</h4>
  <h4>DATE UPLOADED</h4>
  <h4>ACTIONS</h4>
</div>
<div class="body">
  {#each uploaded_fonts as font}
    <div 
      class="row" 
      class:is_editing 
      class:can_delete={canDelete(font)}>
      <span title={font.display_name}>{font.display_name}</span>
      <span title={getStorefrontNames(font)}>{@html getStorefrontNames(font)}</span>
      <span>{formattedUploadDate(font)}</span>
      {#if canDelete(font)}
        {#if selected_to_delete === font.postscript_name}
          <span class="confirm">
            {is_deleting ? 'Deleting...' : 'Are you sure?'}
            <Button size="tiny" kind="delete" scaled_in on:click={() => handleDelete(font)} disabled={is_deleting}>Delete</Button>
            <Button size="tiny" kind="outline" scaled_in on:click={() => selected_to_delete = undefined}>Cancel</Button>
          </span>
        {:else}
          <span>
            <Button size="tiny" kind="default" danger scaled_in on:click={() => selected_to_delete = font.postscript_name}>Delete</Button>
          </span>
        {/if}
      {:else}
        <span class="no_delete">
          <small>Used in storefront</small>
        </span>
      {/if}
    </div>
  {/each}
</div>

<style>
  .warning {
    margin: 2rem -3rem 1rem;
    padding: 1rem 3rem;
    background: var(--c-warning-bg);
    color: var(--c-warning-text);
    border-bottom: 1px solid var(--c-bg-a2);
  }
  .warning :global(b) {
    font-size: 1.4rem;
    margin-right: 0.3rem;
  }
  .body {
    max-height: 34rem;
    overflow-y: scroll;
  }
  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 4rem;
    padding: 0 1rem;
    border-radius: 1rem;
    font-variant: lining-nums;
    transition: all var(--transition-props);
  }
  .row:not(.header):nth-of-type(odd){
    background-color: var(--c-bg);
  }
  .row.is_editing.can_delete:not(.header):hover {
    background-color: var(--c-danger-hover);
  }
  .row span,
  .row h4 {
    flex-grow: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 4rem;
  }
  .row span:nth-child(1),
  .row h4:nth-child(1) {
    width: 30%;
  }
  .row span:nth-child(2),
  .row h4:nth-child(2) {
    width: 25%;
  }
  .row span:nth-child(3),
  .row h4:nth-child(3) {
    width: 20%;
  }
  .row span:nth-child(4),
  .row h4:nth-child(4) {
    width: 25%;
    text-align: right;
  }
  .row span.confirm {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: flex-end;
    color: var(--c-danger);
    font-weight: bold;
  }
  .row span.no_delete {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    color: var(--c-text-faded);
  }
  .row :global(span.faded) {
    color: var(--c-text-faded);
  }
</style> 